/**
 * @fileoverview DriveFilesAction Component - Handles file uploads and displays both local and drive files
 *
 * This component provides a user interface for:
 * - Uploading local files through drag & drop or button click
 * - Displaying uploaded files as thumbnails
 * - Selecting files from Google Drive
 * - Managing file selections and previews
 * - Supporting infinite scroll for drive files
 *
 * @module DriveFilesAction
 * @requires @heroicons/react
 * @requires lodash
 * @requires react
 * @requires react-daisyui
 * @requires react-i18next
 */

import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  FilmIcon,
  FolderIcon,
  SpeakerWaveIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { createHeldBlobUrl, getBlobUrl, releaseBlobUrlHolder, setBlobUrlHolder } from 'src/utils/blobUrlRegistry';

import { Alert, Button, Checkbox, Loading } from 'react-daisyui';

import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { MediaCompressionService } from 'src/services/MediaCompression/MediaCompression.service';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { truncateName } from '../../../../../../../../functions/string.functions';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import useChatActions from '../../../../../../Chat.actions';
import { ChatEnum } from '../../../../../../Chat.enum';
import { useChatContext } from '../../../../../../Chat.hook';
import {
  tAttachment,
  tChatAttachment,
  tDriveFile,
} from '../../../../../../Chat.types';
import { useMessageBarContext } from '../../../../MessageBar.hook';
import { retainDraftBlobAttachments } from '../../../../draftMessages.store';
import { generateVideoPoster } from '../../../Assets/Assets.functions';
import {
  getFileMetaData,
  isVideo,
} from '../../../Thumbnail/Thumbnail.functions';
import { isBlockedMovFile } from './DriveFilesAction.function';
import { useDriveFilesActionContext } from './DriveFilesAction.hook';
import './DriveFilesAction.styles.scss';
import { tDriveFilesActionProps } from './DriveFilesAction.types';
import {
  getAllowedTypesForChannel,
  getMaxFileSizeForChannel,
  isEmptyFile,
} from './components/UploadFileAction/UploadFileAction.constants';

/**
 * Component for handling file uploads and displaying both local and drive files
 * @component
 * @param {tDriveFilesActionProps} props - Component properties
 * @returns {JSX.Element} DriveFilesAction component
 */
export default function DriveFilesAction(props: tDriveFilesActionProps) {
  const { setNewAttachments, setOpenDrive } = props;
  const {
    selectedFiles,
    isLastPage,
    saveOnDrive,
    setSaveOnDrive,
    cursor,
    setCursor,
    setIsLastPage,
    getMoreLeads,
    setSelectedFiles,
  } = useDriveFilesActionContext();
  const { setBlockTextArea } = useChatContext();
  const { sendFileMessage, deleteFile } = useChatActions();
  const { canUserUploadToStorage } = useACLContext();

  const [uploadToStorageError, setUploadToStorageError] = useState(false);
  const { selectedLeadForChatModal } = useModalsContext();

  /** Loading state for file operations */
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  /** Loading state for file preview operations */
  const [loadingForPreviewFiles, setLoadingForPreviewFiles] = useState(false);
  /**
   * Live video-compression progress for the footer bar. Null when no video is
   * being compressed; `percent` reaches 100 right before the S3 upload, where
   * the label flips to "uploading". `current`/`total` label which video in a
   * multi-file batch is encoding.
   */
  const [compressionProgress, setCompressionProgress] = useState<{
    percent: number;
    current: number;
    total: number;
  } | null>(null);
  /** List of selected local files */
  const [localFiles, setLocalFiles] = useState<FileList>(
    new DataTransfer().files,
  );
  const previewHolder = useRef({});
  useEffect(() => {
    const holder = previewHolder.current;
    return () => releaseBlobUrlHolder(holder);
  }, []);

  const updateLocalFiles = (files: FileList) => {
    const urls = Array.from(files).filter((file) => file.type.startsWith('image/'))
      .map((file) => createHeldBlobUrl(file, previewHolder.current));
    setBlobUrlHolder(previewHolder.current, urls);
    setLocalFiles(files);
  };
  /** State to track if a file is being dragged over the drop zone */
  const [isDragging, setIsDragging] = useState(false);
  /** Loading state for fetching more drive files */
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loadingMoreLeads, setLoadingMoreLeads] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isRequestInProgressRef = useRef<boolean>(false);
  const hasLoadedInitialFilesRef = useRef<boolean>(false);
  /**
   * Eager video-compression queue. Videos are compressed the moment they're
   * added (so the work overlaps with the user writing their caption); the queue
   * is drained one file at a time to keep memory and the HW encoder bounded.
   * The send path reuses the cached/in-flight result via MediaCompressionService.
   */
  const eagerQueueRef = useRef<File[]>([]);
  const eagerRunningRef = useRef<boolean>(false);
  const eagerTotalRef = useRef<number>(0);
  const eagerDoneRef = useRef<number>(0);

  /** Chat context values */
  const { textAreaRef, selectedChat, setSelectedChat } = useChatContext();
  /** Message bar context values */
  const {
    setChatAttachments,
    setSelectedAction,
    draftMessages,
    // setMessageBarValue,
  } = useMessageBarContext();
  /** Toast context for showing alerts */
  const { addAlert } = useToastContext();
  /** Translation function */
  const { t } = useTranslation('chat/chat');

  /** Reference to the chat text area */
  const textArea = textAreaRef.current;
  /** Maximum number of files that can be uploaded */
  const MAX_FILES = 30;

  const userHasDriveAvailable = canUserUploadToStorage();
  /** Allowed file types based on the chat integration */
  const allowedTypes = getAllowedTypesForChannel(selectedLeadForChatModal?.chat?.integration).join(',');

  /**
   * Reset initial load ref when chat changes
   * This allows loading files for a new chat if user switches chats without closing the panel
   */
  useEffect(() => {
    hasLoadedInitialFilesRef.current = false;
  }, [selectedChat?.leadId]);

  /**
   * Load drive files when component mounts (user clicks attachment button)
   * Only loads if there are no files already loaded and initial load hasn't happened
   */
  useEffect(() => {
    (async () => {
      // Only load if no files are currently loaded, not already loading, and haven't loaded initial files yet
      if (
        !selectedChat?.driveFiles?.files?.length &&
        !loadingMoreLeads &&
        !hasLoadedInitialFilesRef.current
      ) {
        setLoadingMoreLeads(true);
        try {
          const response = await getMoreLeads('');

          if (typeof response === 'string') {
            setIsLastPage(!response);
            setCursor(response);
            // Mark that initial load has completed
            hasLoadedInitialFilesRef.current = true;
          }
        } catch {
          // Error handling is done in getMoreLeads
        } finally {
          setLoadingMoreLeads(false);
        }
      }
    })();
  }, [
    selectedChat?.driveFiles?.files?.length,
    loadingMoreLeads,
    getMoreLeads,
    setIsLastPage,
    setCursor,
  ]);

  /**
   * Handles the scroll event in the container to load more files
   * when the user reaches near the end of the container (pagination)
   * Also handles initial load when user scrolls with local files attached but no drive files loaded
   */
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Don't fetch drive files when local files are attached (drive files are hidden)
      // Only fetch when drive files are visible (no local files or drive files already loaded)
      if (
        !scrollContainer ||
        isRequestInProgressRef.current ||
        loadingMoreLeads ||
        isLastPage ||
        localFiles.length > 0 // Don't fetch when local files are attached
      )
        return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // If the user has scrolled to 85% of the container, we load more files
      if (scrollPercentage > 0.85) {
        const needsInitialLoad = !cursor && !hasLoadedInitialFilesRef.current;
        const cursorToUse = cursor || '';

        // Mark that there is a request in progress
        isRequestInProgressRef.current = true;
        setLoadingMoreLeads(true);

        getMoreLeads(cursorToUse)
          .then((responseIsLastPage) => {
            if (typeof responseIsLastPage === 'string') {
              setIsLastPage(!responseIsLastPage);
              setCursor(responseIsLastPage);
              // Mark initial load as complete if this was the initial load
              if (needsInitialLoad) {
                hasLoadedInitialFilesRef.current = true;
              }
            }

            setLoadingMoreLeads(false);
            // Indicate that the request has finished
            isRequestInProgressRef.current = false;
          })
          .catch(() => {
            // In case of error, we also release the flag
            setLoadingMoreLeads(false);
            isRequestInProgressRef.current = false;
          });
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [cursor, loadingMoreLeads, isLastPage, getMoreLeads, localFiles.length]);

  /**
   * Initializes data from the DriveFilesActionContext
   */
  useEffect(() => {
    setSelectedItems(selectedFiles.map((file) => file.id));
  }, [selectedFiles]);

  /**
   * Handles file selection through input element
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the file input
   * @returns {void}
   */
  /**
   * Drains the eager-compression queue one video at a time, driving the footer
   * progress bar. Best-effort: warm-up failures are swallowed here so the send
   * path can surface the real error (and its toast) when the user actually sends.
   */
  async function drainEagerCompressionQueue() {
    if (eagerRunningRef.current) return;
    eagerRunningRef.current = true;

    const channel = selectedLeadForChatModal?.chat?.integration;
    try {
      while (eagerQueueRef.current.length > 0) {
        const file = eagerQueueRef.current.shift() as File;
        const { maxSize } = getMaxFileSizeForChannel(channel, file);
        const current = eagerDoneRef.current + 1;
        const total = eagerTotalRef.current;

        try {
          await MediaCompressionService.normalizeFileForChannel({
            file,
            channel,
            maxBytes: maxSize,
            onProgress: (progress) => {
              const percent = Math.round(progress * 100);
              setCompressionProgress((prev) =>
                prev && prev.percent === percent && prev.current === current
                  ? prev
                  : { percent, current, total },
              );
            },
          });
        } catch {
          // best-effort warm-up; the send path re-runs and surfaces errors
        }
        eagerDoneRef.current += 1;
      }
    } finally {
      eagerRunningRef.current = false;
      eagerTotalRef.current = 0;
      eagerDoneRef.current = 0;
      setCompressionProgress(null);
    }
  }

  /**
   * Kicks off compression for newly added oversize videos immediately, so it
   * overlaps with the user composing their message instead of blocking at send.
   * On WAPI every video is rewritten into a fresh MP4 even under the limit
   * (BUG-880), so those are warmed up too.
   */
  function enqueueEagerCompression(files: File[]) {
    const channel = selectedLeadForChatModal?.chat?.integration;
    const videosToCompress = files.filter((file) => {
      if (!file.type.startsWith('video/')) return false;
      const { maxSize } = getMaxFileSizeForChannel(channel, file);
      if (file.size <= maxSize) return channel === eChannelType.WHATSAPP_API;
      return MediaCompressionService.canCompressFile(file);
    });

    if (videosToCompress.length === 0) return;

    eagerQueueRef.current.push(...videosToCompress);
    eagerTotalRef.current += videosToCompress.length;
    void drainEagerCompressionQueue();
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = event.target.files;

    if (newFiles) {
      const dt = new DataTransfer();
      const currentFiles = Array.from(localFiles);
      const totalFiles = [...currentFiles, ...Array.from(newFiles)];

      if (totalFiles.length > MAX_FILES) {
        addAlert({
          type: eAlertType.WARNING,
          message: `${t('driveFilesAction.maxFilesAlt1')}${MAX_FILES}${t('driveFilesAction.maxFilesAlt2')}`,
        });
        return;
      }

      // Check channel-aware file size limits. Oversize images/videos are let
      // through: sendFileMessage compresses them to the channel limit before
      // uploading (see MediaCompressionService).
      const channel = selectedLeadForChatModal?.chat?.integration;
      for (const file of Array.from(newFiles)) {
        if (isEmptyFile(file)) {
          addAlert({
            type: eAlertType.WARNING,
            message: t('driveFilesAction.emptyFile', { fileName: file.name }),
          });
          return;
        }

        const { maxSize, humanLimit } = getMaxFileSizeForChannel(channel, file);

        if (file.size > maxSize && !MediaCompressionService.canCompressFile(file)) {
          const message = t('driveFilesAction.fileSizeExceeded', {
            fileName: file.name,
            maxSize: humanLimit,
          });

          addAlert({
            type: eAlertType.WARNING,
            message,
          });
          return;
        }
      }

      totalFiles.forEach((file) => dt.items.add(file));
      updateLocalFiles(dt.files);
      enqueueEagerCompression(Array.from(newFiles));
    }

    textArea?.focus();
  }

  /**
   * Removes a local file from the preview list
   * @param {number} indexToRemove - Index of the file to remove
   * @returns {void}
   */
  function removeFilePreview(indexToRemove: number) {
    if (!localFiles) return;

    const dataTransfer = new DataTransfer();
    for (let i = 0; i < localFiles.length; i++) {
      if (i !== indexToRemove) {
        dataTransfer.items.add(localFiles[i]);
      }
    }

    textArea?.focus();
    updateLocalFiles(dataTransfer.files);
  }

  /**
   * Processes and sends selected files to the chat
   * @async
   * @returns {Promise<void>}
   */
  async function sendSelectedFiles() {
    const uploadHolder = {};
    try {
      let attachments: tAttachment[] = [];
      let chatAttachments: tChatAttachment[] = [];

      if (draftMessages?.length && selectedChat?.leadId) {
        const currentDraft = draftMessages.findIndex(
          (draft) => draft.leadId === selectedChat?.leadId,
        );
        chatAttachments = draftMessages[currentDraft]?.attachments || [];
        // Convert tChatAttachment[] to tAttachment[] objects
        attachments = (draftMessages[currentDraft]?.attachments || []).map(
          (chatAttachment) => ({
            mediaType: getFileMetaData(chatAttachment.name).mediaType,
            mimeType: chatAttachment.type,
            url: chatAttachment.src,
            filename: chatAttachment.name,
            vcard: null,
            decryption: null,
            duration: null,
            latitude: null,
            longitude: null,
          }),
        );
      }

      setBlockTextArea(true);
      const isPreviewAttachmentFile = !!setNewAttachments && !!setOpenDrive;

      if (isPreviewAttachmentFile) {
        setLoadingForPreviewFiles(true);
      } else {
        setLoading(true);
      }

      const selectedDriveFiles =
        selectedChat?.driveFiles?.files?.filter((file) =>
          selectedItems.includes(file.id),
        ) || [];

      // Only videos hit the (slow) compression path, so the footer bar counts
      // "video N of M" across just the video files in this batch.
      const totalVideos = Array.from(localFiles).filter((file) =>
        file.type.startsWith('video/'),
      ).length;
      let videoOrdinal = 0;

      for (const file of localFiles) {
        try {
          const isVideoFile = file.type.startsWith('video/');
          if (isVideoFile) {
            videoOrdinal += 1;
          }
          const currentVideo = videoOrdinal;

          // Only send with saveOnDrive if the checkbox is checked
          const responseAttachment = (await sendFileMessage(
            file,
            saveOnDrive, // The checkbox controls if it's saved in the shared folder
            true,
            '',
            undefined,
            isVideoFile
              ? (progress) => {
                  const percent = Math.round(progress * 100);
                  setCompressionProgress((prev) =>
                    prev &&
                    prev.percent === percent &&
                    prev.current === currentVideo
                      ? prev
                      : { percent, current: currentVideo, total: totalVideos },
                  );
                }
              : undefined,
          )) as tAttachment;
          if (!responseAttachment.filename) {
            continue;
          }

          const metaData = getFileMetaData(responseAttachment.filename);
          const poster = isVideo(metaData.mimeType)
            ? await generateVideoPoster(file)
            : '';

          const source = responseAttachment.url || createHeldBlobUrl(file, uploadHolder);
          // Name the bytes we actually uploaded, not the bytes the user picked. A
          // transcoded HEIC ships as JPEG, and persisting `IMG_0001.heic` next to
          // `image/jpeg` makes ImageContent's HEIC detector (which ORs on the
          // filename) claim a perfectly good JPEG is undecodable (BUG-712). The
          // drop and paste paths already derive their metadata this way.
          const sentName = responseAttachment.filename;

          chatAttachments.push({
            type: metaData.mimeType,
            src: source,
            ...(responseAttachment.url ? {} : { file }),
            name: sentName,
            size: file.size,
            poster,
          });

          attachments.push({
            mediaType: metaData.mediaType,
            mimeType: metaData.mimeType,
            url: source,
            filename: sentName,
            vcard: null,
            decryption: null,
            duration: null,
            latitude: null,
            longitude: null,
          });
        } catch (error) {
          LLog.error(
            {
              component: LOG_COMPONENTS.CHAT,
              method: 'DriveFilesAction.sendSelectedFiles',
              operation: LOG_OPERATIONS.ERROR,
            },
            `Failed to upload file: ${file.name}`,
            error as unknown as Error,
          );
          continue;
        }
      }

      for (const driveFile of selectedDriveFiles) {
        const file = new File([driveFile.url], driveFile.name, {
          type: getFileMetaData(driveFile.name).mimeType,
        });

        const poster = isVideo(file.name) ? await generateVideoPoster(file) : '';
        const source = driveFile.url || createHeldBlobUrl(file, uploadHolder);
        const metaData = getFileMetaData(file.name);

        chatAttachments.push({
          type: metaData.mimeType,
          src: source,
          name: file.name,
          size: 0,
          poster,
        });

        attachments.push({
          mediaType: metaData.mediaType,
          mimeType: metaData.mimeType,
          url: source,
          filename: file.name,
          vcard: null,
          decryption: null,
          duration: null,
          latitude: null,
          longitude: null,
        });
      }

      retainDraftBlobAttachments(selectedChat?.leadId, chatAttachments);
      setChatAttachments?.((prev) => {
        const existingAttachments = prev || [];
        const existingUrls = new Set(existingAttachments.map((att) => att.src));
        const newUniqueAttachments = chatAttachments.filter(
          (attachment) => !existingUrls.has(attachment.src),
        );
        return [...existingAttachments, ...newUniqueAttachments];
      });
      setNewAttachments?.((prev) => {
        const existingAttachments = prev || [];
        const existingUrls = new Set(existingAttachments.map((att) => att.url));
        const newUniqueAttachments = attachments.filter(
          (attachment) => !existingUrls.has(attachment.url),
        );
        return [...existingAttachments, ...newUniqueAttachments];
      });

      if (isPreviewAttachmentFile) {
        setLoadingForPreviewFiles(false);
      } else {
        setLoading(false);
      }
      setCompressionProgress(null);

      onCloseDriveFileContainer();
      setBlockTextArea(false);
    } finally {
      releaseBlobUrlHolder(uploadHolder);
    }
  }

  /**
   * Handles selection/deselection of drive files
   * @param {string} id - ID of the file to toggle selection
   * @returns {void}
   */
  function handleSelect(id: string) {
    const isSelected = selectedItems.includes(id);
    // Prevent selecting more files if the maximum limit has been reached
    if (
      !isSelected &&
      selectedItems.length + (localFiles?.length || 0) >= MAX_FILES
    ) {
      addAlert({
        type: eAlertType.WARNING,
        message: `${t('driveFilesAction.maxFilesAlt1')}${MAX_FILES}${t('driveFilesAction.maxFilesAlt2')}`,
      });
      return;
    }

    /**
     * Prevent selecting MOV files - this if is temporary.
     */
    const driveFile = selectedChat?.driveFiles?.files?.find(
      (file) => file.id === id,
    );
    if (driveFile && isBlockedMovFile(driveFile)) {
      return;
    }

    setSelectedItems((prev) => {
      const newSelection = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];

      // Update selected attachments
      const attachments =
        selectedChat?.driveFiles?.files
          ?.filter((file) => newSelection.includes(file.id))
          .map((file) => {
            const metaData = getFileMetaData(file.name);

            return {
              filename: file.name,
              url: file.url,
              type: metaData.mimeType,
              size: 0,
              duration: 0,
              decryption: {
                encryptedKey: '',
                iv: '',
              },
              vcard: '',
              latitude: 0,
              longitude: 0,
              mediaType: metaData.mediaType,
              mimeType: metaData.mimeType,
            };
          }) || [];
      setNewAttachments?.((prev) => {
        const existingAttachments = prev || [];
        const existingUrls = new Set(existingAttachments.map((att) => att.url));
        const newUniqueAttachments = attachments.filter(
          (attachment) => !existingUrls.has(attachment.url),
        );
        return [...existingAttachments, ...newUniqueAttachments];
      });

      setSelectedFiles(
        selectedChat?.driveFiles?.files?.filter((file) =>
          newSelection.includes(file.id),
        ) || [],
      );

      return newSelection;
    });
  }

  /**
   * Handles closing of the drive file container
   * @returns {void}
   */
  function onCloseDriveFileContainer() {
    if (setOpenDrive) {
      setOpenDrive(false);
      setSelectedFiles([]);
    } else {
      setSelectedAction(ChatEnum.CHAT);
    }

    if (selectedChat) {
      setSelectedChat({
        ...selectedChat,
        driveFiles: {
          cursor: '',
          files: [],
        },
      });
    }
  }

  const fileCount = (selectedFiles.length || 0) + (localFiles.length || 0);

  /**
   * Handles the deletion of selected files
   * @async
   * @returns {Promise<void>}
   */
  const handleDeleteFiles = async () => {
    setLoading(true);
    try {
      for (const fileId of selectedItems) {
        await deleteFile(fileId, setLoading);
      }
      // Update the file list
      if (selectedChat?.driveFiles?.files) {
        setSelectedChat({
          ...selectedChat,
          driveFiles: {
            ...selectedChat.driveFiles,
            files: selectedChat.driveFiles.files.filter(
              (file) => !selectedItems.includes(file.id),
            ),
          },
        });
      }
      setSelectedItems([]);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  function handleSaveOnDriveChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!userHasDriveAvailable) {
      setSaveOnDrive(false);
      setUploadToStorageError(true);

      return;
    }

    setSaveOnDrive(e.target.checked);
  }

  return (
    <div className="DriveFilesAction__Container">
      {/* Header */}
      <Alert className="DriveFilesAction__Header">
        <div className="DriveFilesAction__Header__IconContainer">
          <FolderIcon className="DriveFilesAction__Header__FolderIcon" />
          <span>{t('driveFilesAction.sharedDir')}</span>
        </div>
        <XMarkIcon
          className="DriveFilesAction__Header__CloseIcon"
          onClick={onCloseDriveFileContainer}
        />
      </Alert>

      {/* Content - Thumbnail list */}
      <div className="DriveFilesAction__Content">
        <div
          ref={scrollContainerRef}
          className="DriveFilesAction__ScrollContainer"
          onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);

            // Calcular dimensiones del contenedor
            const container = e.currentTarget;
            const rect = container.getBoundingClientRect();
            const dropArea = document.querySelector(
              '.DriveFilesAction__DropArea',
            );

            if (dropArea && dropArea instanceof HTMLElement) {
              // Aplicar dimensiones del contenedor al drop area
              dropArea.style.width = `${rect.width}px`;
              dropArea.style.height = `${rect.height}px`;
              dropArea.style.top = `${rect.top}px`;
              dropArea.style.left = `${rect.left}px`;
              dropArea.style.position = 'fixed';
            }
          }}
          onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            // Verificar si el elemento relacionado pertenece al componente DriveFilesAction
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (
              relatedTarget &&
              !relatedTarget.className
                ?.toString()
                .includes('DriveFilesAction__')
            ) {
              setIsDragging(false);
            }
          }}
          onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDrop={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            if (!canUserUploadToStorage()) {
              setUploadToStorageError(true);

              return;
            }

            const dt = new DataTransfer();
            const currentFiles = Array.from(localFiles);
            const newFiles = Array.from(e.dataTransfer.files);
            const totalFiles = [...currentFiles, ...newFiles];

            /* Check if there are MOV files - this is temporary until we have a better solution in backend */
            for (const file of totalFiles) {
              const isMov = file.name.toLowerCase().endsWith('.mov');

              if (isMov) {
                addAlert({
                  type: eAlertType.WARNING,
                  message: t('driveFilesAction.fileTypeNotSupported', {
                    fileName: file.name,
                  }),
                });
                return;
              }
            }

            if (totalFiles.length > MAX_FILES) {
              addAlert({
                type: eAlertType.WARNING,
                message: `${t('driveFilesAction.maxFilesAlt1')}${MAX_FILES}${t('driveFilesAction.maxFilesAlt2')}`,
              });
              return;
            }

            // Check channel-aware file size limits. Oversize images/videos
            // are let through: sendFileMessage compresses them to the channel
            // limit before uploading (see MediaCompressionService).
            const channel = selectedLeadForChatModal?.chat?.integration;
            for (const file of newFiles) {
              if (isEmptyFile(file)) {
                addAlert({
                  type: eAlertType.WARNING,
                  message: t('driveFilesAction.emptyFile', {
                    fileName: file.name,
                  }),
                });
                return;
              }

              const { maxSize, humanLimit } = getMaxFileSizeForChannel(channel, file);

              if (
                file.size > maxSize &&
                !MediaCompressionService.canCompressFile(file)
              ) {
                const message = t('driveFilesAction.fileSizeExceeded', {
                  fileName: file.name,
                  maxSize: humanLimit,
                });

                addAlert({
                  type: eAlertType.WARNING,
                  message,
                });
                return;
              }
            }

            totalFiles.forEach((file) => dt.items.add(file));
            updateLocalFiles(dt.files);
            enqueueEagerCompression(newFiles);
            textArea?.focus();
          }}
        >
          {uploadToStorageError ? (
            <Alert
              icon={
                <ExclamationTriangleIcon className="DriveFilesAction__ErrorBanner__Icon" />
              }
              className="DriveFilesAction__ErrorBanner"
            >
              <div className="DriveFilesAction__ErrorBanner__Body">
                <strong>{t('driveFilesAction.storageLimitReached')}</strong>
                <span>{t('driveFilesAction.storageLimitMessage')}</span>
              </div>
            </Alert>
          ) : null}

          {/* Drop area */}
          <div
            className={`DriveFilesAction__DropArea ${
              isDragging
                ? 'DriveFilesAction__DropArea--active'
                : 'DriveFilesAction__DropArea--inactive'
            }`}
          >
            <div className="DriveFilesAction__DropArea__Content">
              <ArrowUpTrayIcon className="DriveFilesAction__DropArea__Icon" />
              <h3 className="DriveFilesAction__DropArea__Title">
                {t('driveFilesAction.dropFileHere')}
              </h3>
              <p className="DriveFilesAction__DropArea__Text">
                {t('driveFilesAction.maxFilesCount', { maxFiles: MAX_FILES })}
              </p>
            </div>
          </div>

          {/* Loading indicator - shows when fetching drive files initially */}
          {loadingMoreLeads &&
          !selectedChat?.driveFiles?.files?.length &&
          !localFiles.length ? (
            <div className="DriveFilesAction__LoadingContainer">
              <Loading size="lg" color="primary" />
              <p className="DriveFilesAction__LoadingText">
                {t('driveFilesAction.loadingFiles')}
              </p>
            </div>
          ) : null}

          {!localFiles.length && !loadingMoreLeads ? (
            <div className="DriveFilesAction__UploadButton">
              <label
                htmlFor="file-upload"
                className="DriveFilesAction__UploadButton__Label"
              >
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  className="DriveFilesAction__UploadButton__Input"
                  onChange={handleFileChange}
                  accept={allowedTypes}
                />
                <ArrowUpTrayIcon className="DriveFilesAction__UploadButton__Icon" />
                <span className="DriveFilesAction__UploadButton__Text">
                  {t('driveFilesAction.uploadFile')}
                </span>
              </label>
            </div>
          ) : null}

          {/* Local files list */}
          {localFiles.length > 0 ? (
            <>
              {/* Save on drive checkbox */}
              <div className="DriveFilesAction__SaveCheckbox">
                <label className="DriveFilesAction__SaveCheckbox__Label">
                  <input
                    type="checkbox"
                    className="DriveFilesAction__SaveCheckbox__Input"
                    checked={saveOnDrive}
                    onChange={handleSaveOnDriveChange}
                  />
                  <span>{t('uploadFileAction.saveOnDriveLabel')}</span>
                </label>
              </div>

              {Array.from(localFiles).map((file, index) => (
                <div
                  key={`local-${index}`}
                  className="DriveFilesAction__FileItem group"
                >
                  <div className="DriveFilesAction__FileItem__Preview">
                    {file.type.startsWith('image/') ? (
                      <img
                        src={getBlobUrl(file)}
                        alt={file.name}
                        className="DriveFilesAction__FileItem__ImageContent"
                      />
                    ) : file.type.startsWith('video/') ? (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <FilmIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    ) : file.type.startsWith('audio/') ? (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <SpeakerWaveIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    ) : (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <DocumentTextIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    )}
                  </div>
                  {/* Tooltip */}
                  <div className="DriveFilesAction__FileItem__Tooltip">
                    {truncateName(file.name, 23, 18)}
                  </div>
                  <button
                    onClick={() => removeFilePreview(index)}
                    className="DriveFilesAction__FileItem__RemoveButton"
                  >
                    <XMarkIcon className="DriveFilesAction__FileItem__RemoveIcon" />
                  </button>
                </div>
              ))}
            </>
          ) : (
            /* Drive files list */
            selectedChat?.driveFiles?.files?.map(
              (file: tDriveFile, index: number) => (
                <div
                  key={file.id + index}
                  className="DriveFilesAction__FileItem group"
                  onClick={() => handleSelect(file.id)}
                >
                  {/* Tooltip */}
                  <div className="DriveFilesAction__FileItem__Tooltip">
                    {truncateName(file.name, 23, 18)}
                  </div>
                  <div className="DriveFilesAction__FileItem__Preview">
                    {file.mimeType?.startsWith('image/') ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="DriveFilesAction__FileItem__ImageContent"
                      />
                    ) : file.mimeType?.startsWith('video/') ? (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <FilmIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    ) : file.mimeType?.startsWith('audio/') ? (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <SpeakerWaveIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    ) : (
                      <div className="DriveFilesAction__FileItem__FileContent">
                        <DocumentTextIcon className="DriveFilesAction__FileItem__FileIcon" />
                      </div>
                    )}
                  </div>
                  <div className="DriveFilesAction__FileItem__CheckboxWrapper">
                    <Checkbox
                      checked={selectedItems.includes(file.id)}
                      readOnly
                      size="sm"
                      color="primary"
                      disabled={
                        isBlockedMovFile(
                          file,
                        ) /** This validation is temporary */ ||
                        (!selectedItems.includes(file.id) &&
                          selectedItems.length + (localFiles?.length || 0) >=
                            MAX_FILES)
                      }
                    />
                  </div>
                </div>
              ),
            )
          )}

          {/* Loading indicator for pagination (when loading more files while scrolling) */}
          {loadingMoreLeads &&
          selectedChat?.driveFiles?.files?.length &&
          !isLastPage ? (
            <div className="DriveFilesAction__PaginationLoading">
              <Loading size="sm" color="primary" />
              <span className="DriveFilesAction__PaginationLoadingText">
                {t('driveFilesAction.loadingMoreFiles')}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <Alert className="DriveFilesAction__Footer">
        <div className="DriveFilesAction__Footer__InfoText">
          {compressionProgress ? (
            <div className="flex w-full flex-col gap-1">
              <span className="text-xs font-medium text-base-content/80">
                {compressionProgress.total > 1
                  ? t('driveFilesAction.compressingVideoCount', {
                      current: compressionProgress.current,
                      total: compressionProgress.total,
                      percent: compressionProgress.percent,
                    })
                  : t('driveFilesAction.compressingVideoProgress', {
                      percent: compressionProgress.percent,
                    })}
              </span>
              <progress
                className="progress progress-primary w-full"
                value={compressionProgress.percent}
                max={100}
              />
            </div>
          ) : (
            <span>
              {t('driveFilesAction.fileSizeInfo', { maxFiles: MAX_FILES })}
            </span>
          )}
        </div>
        <div className="DriveFilesAction__Footer__Actions">
          {selectedItems.length > 0 && (
            <Button
              onClick={() => setShowDeleteModal(true)}
              color="ghost"
              size="sm"
              className="DriveFilesAction__Footer__DeleteButton"
              disabled={loading}
            >
              <TrashIcon className="DriveFilesAction__Footer__DeleteIcon" />
              {t('driveFilesAction.deleteFilesButton')}
            </Button>
          )}
          <Button
            onClick={sendSelectedFiles}
            color="primary"
            disabled={
              loading ||
              loadingForPreviewFiles ||
              loadingMoreLeads ||
              (isEmpty(selectedItems) && isEmpty(localFiles))
            }
            size="sm"
            className="DriveFilesAction__Footer__SendButton"
          >
            {loading || loadingForPreviewFiles ? (
              <Loading size="sm" />
            ) : (
              t('driveFilesAction.attachFilesButton') +
              (selectedItems.length ? ` (${fileCount})` : '')
            )}
          </Button>
        </div>
      </Alert>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="DriveFilesAction__Modal">
          <div className="DriveFilesAction__Modal__Content">
            <h3 className="DriveFilesAction__Modal__Title">
              {t('driveFilesAction.deleteConfirmTitle')}
            </h3>
            <p className="DriveFilesAction__Modal__Message">
              {t('driveFilesAction.deleteConfirmMessage')}
            </p>
            <div className="DriveFilesAction__Modal__Actions">
              <Button
                onClick={() => setShowDeleteModal(false)}
                color="ghost"
                size="sm"
                disabled={loading}
              >
                {t('driveFilesAction.cancelButton')}
              </Button>
              <Button
                onClick={handleDeleteFiles}
                color="error"
                size="sm"
                disabled={loading}
              >
                {loading ? (
                  <Loading size="sm" />
                ) : (
                  t('driveFilesAction.deleteButton')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
